import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  getBackofficeDisplayTimezone,
  toDayjsForBackofficeDisplay,
} from "./dateUtils";
import {
  TIMELINE_LANES,
  TimelineLaneEnum,
  TimelineOutcomeEnum,
} from "@/domain/troubleshooting.enum";
import {
  DecisionCondition,
  GeofenceSyncDto,
  TimelineEvent,
  TimelineEventDetail,
  TimelineEventOrigin,
  ValidationJourney,
} from "@/domain/troubleshooting.interface";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * COYN-1507: pure helpers of the participant timeline screen (window maths,
 * swimlane layout, filters and the deep-link URL state).
 */

export type TimelineZoom = "day" | "week";
export type TimelineTab = "timeline" | "journeys";

export const TIMELINE_ROUTE = "/troubleshooting/timeline";
/** Contract §4: the timeline/journeys API accepts at most 31 days. */
export const MAX_WINDOW_DAYS = 31;
export const ZOOM_DAYS: Record<TimelineZoom, number> = { day: 1, week: 7 };

const DATE_PARAM = /^\d{4}-\d{2}-\d{2}$/;
const TABS: TimelineTab[] = ["timeline", "journeys"];

export interface TimelineWindow {
  /** ISO instant sent to the API. */
  from: string;
  /** ISO instant sent to the API. */
  to: string;
  fromMs: number;
  toMs: number;
  /** Calendar days (YYYY-MM-DD, account timezone) covered by the window. */
  days: string[];
}

export interface TimelineUrlState {
  participantId: string;
  /** Anchor day (YYYY-MM-DD) in the account timezone; the window ends on it. */
  date: string;
  zoom: TimelineZoom;
  /** Empty = all lanes. */
  lanes: TimelineLaneEnum[];
  failuresOnly: boolean;
  eventId: string;
  geofenceId: string;
  tab: TimelineTab;
  /** Journey to expand and highlight on the Journeys tab ("Trace this event"). */
  journeyId: string;
  /** Live mode (`live=1`); only effective for today in Day zoom. */
  live: boolean;
}

export const isValidDateParam = (value: string | null | undefined): boolean =>
  !!value &&
  DATE_PARAM.test(value) &&
  dayjs(value, "YYYY-MM-DD", true).isValid();

/** Today's calendar day in the account display timezone. */
export const todayInAccountTz = (): string =>
  dayjs().tz(getBackofficeDisplayTimezone()).format("YYYY-MM-DD");

/** Calendar day (account timezone) of an ISO instant. */
export const dateInAccountTz = (iso: string | undefined): string => {
  const d = toDayjsForBackofficeDisplay(iso);
  return d ? d.format("YYYY-MM-DD") : todayInAccountTz();
};

const startOfDayInTz = (date: string) =>
  dayjs.tz(`${date}T00:00:00`, getBackofficeDisplayTimezone());

const endOfDayInTz = (date: string) =>
  dayjs.tz(`${date}T23:59:59.999`, getBackofficeDisplayTimezone());

/**
 * Window that ends on `date` (inclusive): one calendar day for "day", the
 * last seven calendar days for "week". Days are calendar days in the account
 * timezone, so DST changes never shift the boundaries.
 */
export const computeTimelineWindow = (
  date: string,
  zoom: TimelineZoom,
): TimelineWindow => {
  const anchor = isValidDateParam(date) ? date : todayInAccountTz();
  const span = Math.min(ZOOM_DAYS[zoom] ?? 1, MAX_WINDOW_DAYS);
  const days: string[] = [];
  for (let i = span - 1; i >= 0; i -= 1) {
    days.push(dayjs(anchor).subtract(i, "day").format("YYYY-MM-DD"));
  }
  const start = startOfDayInTz(days[0]);
  const end = endOfDayInTz(anchor);
  return {
    from: start.toISOString(),
    to: end.toISOString(),
    fromMs: start.valueOf(),
    toMs: end.valueOf(),
    days,
  };
};

/** Move the anchor day one window back (-1) or forward (+1). */
export const shiftDate = (
  date: string,
  zoom: TimelineZoom,
  direction: 1 | -1,
): string =>
  dayjs(isValidDateParam(date) ? date : todayInAccountTz())
    .add(direction * ZOOM_DAYS[zoom], "day")
    .format("YYYY-MM-DD");

export const positionPercent = (
  iso: string,
  fromMs: number,
  toMs: number,
): number => {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t) || toMs <= fromMs) return 0;
  const pct = ((t - fromMs) / (toMs - fromMs)) * 100;
  return Math.min(100, Math.max(0, pct));
};

export interface AxisTick {
  pct: number;
  label: string;
}

/** Every 3 hours for a day, one tick per day for a week. */
export const buildAxisTicks = (
  window: TimelineWindow,
  zoom: TimelineZoom,
): AxisTick[] => {
  const tz = getBackofficeDisplayTimezone();
  if (zoom === "week") {
    return window.days.map((day) => {
      const start = dayjs.tz(`${day}T00:00:00`, tz);
      return {
        pct: positionPercent(start.toISOString(), window.fromMs, window.toMs),
        label: start.format("ddd D MMM"),
      };
    });
  }
  const day = window.days[window.days.length - 1];
  return [0, 3, 6, 9, 12, 15, 18, 21].map((hour) => {
    const hh = String(hour).padStart(2, "0");
    const at = dayjs.tz(`${day}T${hh}:00:00`, tz);
    return {
      pct: positionPercent(at.toISOString(), window.fromMs, window.toMs),
      label: `${hh}:00`,
    };
  });
};

/** Marker diameter on the swimlane and the gap below which markers merge. */
export const TIMELINE_MARKER_SIZE_PX = 22;
export const TIMELINE_CLUSTER_GAP_PX = 4;
/** Lane track width used until the real one is measured. */
export const DEFAULT_TRACK_WIDTH_PX = 558;

/** One marker of a lane: a single event or a cluster of close events. */
export interface LaneMarker {
  key: string;
  /** Sorted by time; the marker sits at the first one. */
  events: TimelineEvent[];
  pct: number;
  x: number;
}

/**
 * One row per lane: events whose markers would touch on screen (centers
 * closer than marker size + gap, in pixels of the measured track) merge into
 * a cluster anchored at its first event. Anchors are therefore always at
 * least that distance apart, so markers never overlap.
 */
export const clusterLaneEvents = (
  events: TimelineEvent[],
  fromMs: number,
  toMs: number,
  trackWidthPx: number,
  minDistancePx: number = TIMELINE_MARKER_SIZE_PX + TIMELINE_CLUSTER_GAP_PX,
): LaneMarker[] => {
  const width = trackWidthPx > 0 ? trackWidthPx : DEFAULT_TRACK_WIDTH_PX;
  const sorted = [...events].sort(
    (a, b) =>
      new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime(),
  );
  const markers: LaneMarker[] = [];
  sorted.forEach((event) => {
    const pct = positionPercent(event.occurredAt, fromMs, toMs);
    const x = (pct / 100) * width;
    const current = markers[markers.length - 1];
    if (current && x - current.x < minDistancePx) {
      current.events.push(event);
    } else {
      markers.push({ key: event.id, events: [event], pct, x });
    }
  });
  return markers;
};

/** Severity used to colour a cluster: FAILED > SKIPPED > OK > INFO. */
const OUTCOME_SEVERITY: Record<TimelineOutcomeEnum, number> = {
  [TimelineOutcomeEnum.FAILED]: 3,
  [TimelineOutcomeEnum.SKIPPED]: 2,
  [TimelineOutcomeEnum.OK]: 1,
  [TimelineOutcomeEnum.INFO]: 0,
};

export const worstOutcome = (events: TimelineEvent[]): TimelineOutcomeEnum =>
  events.reduce<TimelineOutcomeEnum>(
    (worst, e) =>
      (OUTCOME_SEVERITY[e.outcome] ?? 0) > (OUTCOME_SEVERITY[worst] ?? 0)
        ? e.outcome
        : worst,
    TimelineOutcomeEnum.INFO,
  );

/** "Failures only" keeps everything that did not happen as expected. */
export const isFailureOutcome = (outcome: TimelineOutcomeEnum): boolean =>
  outcome === TimelineOutcomeEnum.FAILED ||
  outcome === TimelineOutcomeEnum.SKIPPED;

export const filterTimelineEvents = (
  events: TimelineEvent[],
  filters: { lanes: TimelineLaneEnum[]; failuresOnly: boolean },
): TimelineEvent[] =>
  events.filter(
    (e) =>
      (filters.lanes.length === 0 || filters.lanes.includes(e.lane)) &&
      (!filters.failuresOnly || isFailureOutcome(e.outcome)),
  );

export const isEventFocused = (
  event: TimelineEvent,
  focus: { eventId?: string; geofenceId?: string },
): boolean =>
  (!!focus.eventId && event.id === focus.eventId) ||
  (!!focus.geofenceId && event.links?.geofenceId === focus.geofenceId);

const parseLanes = (raw: string | null): TimelineLaneEnum[] => {
  if (!raw) return [];
  const requested = raw.split(",").map((l) => l.trim());
  return TIMELINE_LANES.filter((lane) => requested.includes(lane));
};

export const parseTimelineSearchParams = (
  params: URLSearchParams,
): TimelineUrlState => {
  const date = params.get("date");
  const tab = params.get("tab") as TimelineTab | null;
  return {
    participantId: params.get("participantId") || "",
    date: isValidDateParam(date) ? (date as string) : todayInAccountTz(),
    zoom: params.get("zoom") === "week" ? "week" : "day",
    lanes: parseLanes(params.get("lanes")),
    failuresOnly: params.get("failuresOnly") === "true",
    eventId: params.get("eventId") || "",
    geofenceId: params.get("geofenceId") || "",
    tab: tab && TABS.includes(tab) ? tab : "timeline",
    journeyId: params.get("journeyId") || "",
    live: params.get("live") === "1",
  };
};

/** Serialize the URL state, omitting defaults so links stay short. */
export const buildTimelineSearch = (
  state: Partial<TimelineUrlState>,
): string => {
  const params = new URLSearchParams();
  if (state.participantId) params.set("participantId", state.participantId);
  if (state.date && isValidDateParam(state.date))
    params.set("date", state.date);
  if (state.zoom === "week") params.set("zoom", "week");
  if (
    state.lanes &&
    state.lanes.length > 0 &&
    state.lanes.length < TIMELINE_LANES.length
  ) {
    params.set("lanes", state.lanes.join(","));
  }
  if (state.failuresOnly) params.set("failuresOnly", "true");
  if (state.eventId) params.set("eventId", state.eventId);
  if (state.geofenceId) params.set("geofenceId", state.geofenceId);
  if (state.tab && state.tab !== "timeline") params.set("tab", state.tab);
  if (state.journeyId) params.set("journeyId", state.journeyId);
  if (state.live) params.set("live", "1");
  return params.toString();
};

export const buildTimelineUrl = (state: Partial<TimelineUrlState>): string => {
  const qs = buildTimelineSearch(state);
  return qs ? `${TIMELINE_ROUTE}?${qs}` : TIMELINE_ROUTE;
};

export const formatBytes = (bytes?: number): string => {
  if (bytes === undefined || bytes === null || Number.isNaN(bytes)) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Contract §7: a short-circuited condition is `passed: false` + this detail. */
export const NOT_EVALUATED_DETAIL = "not evaluated";

export const isConditionNotEvaluated = (c: DecisionCondition): boolean =>
  !c.passed && (c.detail || "").trim().toLowerCase() === NOT_EVALUATED_DETAIL;

const significantTokens = (value: string): string[] =>
  value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length >= 4);

/**
 * Indexes of the condition(s) behind the primary reason. The backend flags
 * them with `primary: true` (contract §8.5). When no condition carries the
 * flag, fall back to evaluated failures that share a word with `reasonCode`
 * (e.g. `hasPendingValidation` ↔ `PENDING_VALIDATION_EXISTS`), then to the
 * first evaluated failure.
 */
export const primaryConditionIndexes = (
  conditions: DecisionCondition[],
  reasonCode?: string,
): number[] => {
  const flagged = conditions
    .map((condition, index) => (condition.primary === true ? index : -1))
    .filter((index) => index >= 0);
  if (flagged.length > 0) return flagged;

  const failing = conditions
    .map((condition, index) => ({ condition, index }))
    .filter(
      ({ condition }) =>
        !condition.passed && !isConditionNotEvaluated(condition),
    );
  if (failing.length === 0) return [];
  const reasonTokens = new Set(significantTokens(reasonCode || ""));
  const matching = failing.filter(({ condition }) =>
    significantTokens(condition.key).some((token) => reasonTokens.has(token)),
  );
  return (matching.length > 0 ? matching : [failing[0]]).map(
    ({ index }) => index,
  );
};

const isOrigin = (value: unknown): value is TimelineEventOrigin =>
  value === "APP" || value === "SERVER";

/**
 * Event origin: the top-level `origin` (contract §8.4) wins; `payload.origin`
 * is the fallback for events that predate it.
 */
export const readEventOrigin = (event: {
  origin?: unknown;
  payload?: Record<string, unknown>;
}): TimelineEventOrigin | undefined => {
  if (isOrigin(event.origin)) return event.origin;
  const fromPayload = event.payload?.origin;
  return isOrigin(fromPayload) ? fromPayload : undefined;
};

/**
 * "Trace this event" (contract §11.4): the journey whose steps reference the
 * event, matched by `JourneyStep.eventId`.
 */
export const findJourneyForEvent = (
  journeys: ValidationJourney[] | undefined,
  eventId: string | undefined,
): ValidationJourney | undefined =>
  eventId
    ? journeys?.find((journey) =>
        journey.steps.some((step) => step.eventId === eventId),
      )
    : undefined;

/**
 * Contract §15: a 403 from the troubleshooting API means the backend did not
 * grant PART_TIMELINE_VIEW (the service keeps the HTTP status on its errors).
 */
export const isForbiddenError = (error: unknown): boolean =>
  (error as { status?: number } | null)?.status === 403;

/**
 * Live mode: default refresh period (the `participantTimeline` setting's
 * `liveRefreshSeconds` overrides it) and the cap of the error back-off.
 */
export const LIVE_INTERVAL_MS = 30_000;
export const LIVE_MAX_BACKOFF_MS = 5 * 60_000;

/** Live mode only makes sense for today (account timezone) in Day zoom. */
export const isLiveEligible = (
  state: Pick<TimelineUrlState, "date" | "zoom">,
): boolean => state.zoom === "day" && state.date === todayInAccountTz();

/**
 * Turn Live off when the window leaves today or switches to Week, so the URL
 * never keeps `live=1` for a window that cannot be live.
 */
export const withLiveRule = (state: TimelineUrlState): TimelineUrlState =>
  state.live && !isLiveEligible(state) ? { ...state, live: false } : state;

/**
 * Live refresh period after `consecutiveFailures` failed refreshes, starting
 * from the configured interval and capped at 5 min.
 */
export const liveBackoffMs = (
  consecutiveFailures: number,
  baseMs: number = LIVE_INTERVAL_MS,
): number =>
  consecutiveFailures <= 0
    ? baseMs
    : Math.min(baseMs * 2 ** consecutiveFailures, LIVE_MAX_BACKOFF_MS);

/** Contract §16.1: platform json Setting read by the backoffice and backend. */
export const PARTICIPANT_TIMELINE_SETTING = "participantTimeline";

export interface ParticipantTimelineConfig {
  refreshTokenValidityDays: number;
  sessionStaleHours: number;
  deviceMatchWindowSeconds: number;
  deviceEventsNotReceivedDays: number;
  liveRefreshSeconds: number;
  /** §17.3: SDK centre vs address tolerance (computed by the backend). */
  centerMatchToleranceMeters: number;
}

/** Default and safe range of every key (contract §16.1). */
const PARTICIPANT_TIMELINE_LIMITS: Record<
  keyof ParticipantTimelineConfig,
  { default: number; min: number; max: number }
> = {
  refreshTokenValidityDays: { default: 30, min: 1, max: 3650 },
  sessionStaleHours: { default: 72, min: 1, max: 720 },
  deviceMatchWindowSeconds: { default: 60, min: 5, max: 600 },
  deviceEventsNotReceivedDays: { default: 7, min: 1, max: 31 },
  liveRefreshSeconds: { default: 30, min: 10, max: 300 },
  centerMatchToleranceMeters: { default: 25, min: 1, max: 1000 },
};

export const DEFAULT_PARTICIPANT_TIMELINE_CONFIG = Object.fromEntries(
  Object.entries(PARTICIPANT_TIMELINE_LIMITS).map(([key, limit]) => [
    key,
    limit.default,
  ]),
) as unknown as ParticipantTimelineConfig;

const toFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === "number")
    return Number.isFinite(value) ? value : undefined;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
};

/**
 * Parse the `participantTimeline` setting value, which may arrive as an
 * object or as JSON text (type `json`). A missing setting, a missing key or a
 * non-numeric value falls back to the default; every value is clamped.
 */
export const parseParticipantTimelineSetting = (
  raw: unknown,
): ParticipantTimelineConfig => {
  let value: unknown = raw;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      value = undefined;
    }
  }
  const source =
    value && typeof value === "object" && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  return Object.fromEntries(
    Object.entries(PARTICIPANT_TIMELINE_LIMITS).map(([key, limit]) => {
      const n = toFiniteNumber(source[key]);
      return [
        key,
        n === undefined
          ? limit.default
          : Math.min(limit.max, Math.max(limit.min, n)),
      ];
    }),
  ) as unknown as ParticipantTimelineConfig;
};

/* ------------------------------------------------------------------ */
/* §17: location for troubleshooting (internal, admin-only).           */
/* ------------------------------------------------------------------ */

export interface MapPoint {
  lat: number;
  lng: number;
  label: string;
}

/** Props for `GeofenceMap`, built without Leaflet so they can be tested. */
export interface TroubleshootingMapModel {
  primary?: MapPoint;
  secondary?: MapPoint;
  event: MapPoint;
  activeCenter?: MapPoint;
  radiusMeters?: number;
  extraPoints: Array<MapPoint & { color: string; legend: string }>;
  extraCircles: Array<{
    center: MapPoint;
    radiusMeters: number;
    color: string;
    dashed?: boolean;
  }>;
}

/** Same colours as the GeofenceMap address pins, plus SDK centre dots. */
export const MAP_COLORS = {
  primaryAddress: "#1976d2",
  secondaryAddress: "#2e7d32",
  sdkPrimary: "#6253A2",
  sdkSecondary: "#B26A00",
};

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
};

const toBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === "boolean") return value;
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
};

const point = (lat: unknown, lng: unknown, label: string) => {
  const la = toNumber(lat);
  const ln = toNumber(lng);
  return la === undefined || ln === undefined
    ? undefined
    : { lat: la, lng: ln, label };
};

export interface SyncMapLabels {
  primaryAddress: string;
  secondaryAddress: string;
  sdkPrimary: string;
  sdkSecondary: string;
  lastPosition: string;
}

/**
 * Geofence sync card map: registered addresses (pins + radius circles), the
 * centres the SDK actually watches (dots; dashed radius when they do not
 * match the address) and the last position (event pin). Without a last
 * position the first SDK centre, else the first address, becomes the pin.
 * Null when nothing has coordinates.
 */
export const buildSyncMapModel = (
  sync: GeofenceSyncDto,
  labels: SyncMapLabels,
): TroubleshootingMapModel | null => {
  const addresses = sync.registeredAddresses;
  let primary = point(
    addresses?.primary?.latitude,
    addresses?.primary?.longitude,
    addresses?.primary?.line || labels.primaryAddress,
  );
  let secondary = point(
    addresses?.secondary?.latitude,
    addresses?.secondary?.longitude,
    addresses?.secondary?.line || labels.secondaryAddress,
  );
  const radius = toNumber(sync.sdkCenters?.radius);
  const extraPoints: TroubleshootingMapModel["extraPoints"] = [];
  const sdkPrimary = point(
    sync.sdkCenters?.primary?.latitude,
    sync.sdkCenters?.primary?.longitude,
    labels.sdkPrimary,
  );
  const sdkSecondary = point(
    sync.sdkCenters?.secondary?.latitude,
    sync.sdkCenters?.secondary?.longitude,
    labels.sdkSecondary,
  );
  if (sdkPrimary) {
    extraPoints.push({
      ...sdkPrimary,
      color: MAP_COLORS.sdkPrimary,
      legend: labels.sdkPrimary,
    });
  }
  if (sdkSecondary) {
    extraPoints.push({
      ...sdkSecondary,
      color: MAP_COLORS.sdkSecondary,
      legend: labels.sdkSecondary,
    });
  }

  const extraCircles: TroubleshootingMapModel["extraCircles"] = [];
  if (radius && radius > 0) {
    if (primary) {
      extraCircles.push({
        center: primary,
        radiusMeters: radius,
        color: MAP_COLORS.primaryAddress,
      });
    }
    if (secondary) {
      extraCircles.push({
        center: secondary,
        radiusMeters: radius,
        color: MAP_COLORS.secondaryAddress,
      });
    }
    // Where the device really triggers ENTER/EXIT, when it is elsewhere.
    if (sync.sdkCentersMatchAddress === false) {
      extraPoints.forEach((p) =>
        extraCircles.push({
          center: p,
          radiusMeters: radius,
          color: p.color,
          dashed: true,
        }),
      );
    }
  }

  let event = sync.lastPosition
    ? point(
        sync.lastPosition.latitude,
        sync.lastPosition.longitude,
        labels.lastPosition,
      )
    : undefined;
  if (!event && extraPoints.length > 0) {
    const [first, ...rest] = extraPoints;
    event = { lat: first.lat, lng: first.lng, label: first.label };
    extraPoints.splice(0, extraPoints.length, ...rest);
  }
  if (!event && primary) {
    event = primary;
    primary = undefined;
  }
  if (!event && secondary) {
    event = secondary;
    secondary = undefined;
  }
  if (!event) return null;

  return { primary, secondary, event, extraPoints, extraCircles };
};

/** App events whose data carries a position (contract §17.2). */
export const LOCATED_APP_EVENT_TYPES = [
  "DEVICE_ENTER",
  "DEVICE_EXIT",
  "POSITION_SNAPSHOT",
];

export interface EventLocation {
  lat: number;
  lng: number;
  accuracy?: number;
  radius?: number;
  distance?: number;
  inside?: boolean;
  /** primary / secondary when the event says which geofence it is about. */
  geofence?: "primary" | "secondary";
  primaryCenter?: { lat: number; lng: number };
  secondaryCenter?: { lat: number; lng: number };
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

/**
 * Position of a DEVICE_ENTER/EXIT or POSITION_SNAPSHOT app event, or of a
 * backend GEOFENCE event, read from its sanitized payload (flat, or under
 * `data` for app events). Null when the event has no coordinates.
 */
export const extractEventLocation = (
  detail: Pick<TimelineEventDetail, "source" | "type" | "payload">,
): EventLocation | null => {
  const eligible =
    detail.source === "GEOFENCE" ||
    (detail.source === "APP_EVENT" &&
      LOCATED_APP_EVENT_TYPES.includes(detail.type));
  if (!eligible) return null;
  const payload = isPlainObject(detail.payload) ? detail.payload : {};
  const bag: Record<string, unknown> = {
    ...payload,
    ...(isPlainObject(payload.data) ? payload.data : {}),
  };
  const lat = toNumber(bag.latitude) ?? toNumber(bag.lastLatitude);
  const lng = toNumber(bag.longitude) ?? toNumber(bag.lastLongitude);
  if (lat === undefined || lng === undefined) return null;

  const radius =
    toNumber(bag.radiusMeters) ??
    toNumber(bag.radius) ??
    toNumber(bag.registeredRadius);
  const distance = toNumber(bag.distanceToCenterMeters);
  const inside =
    toBoolean(bag.insideGeofence) ??
    (distance !== undefined && radius !== undefined
      ? distance <= radius
      : undefined);
  const which = String(
    bag.currentGeofence ?? bag.addressType ?? bag.identifier ?? "",
  ).toLowerCase();
  const centre = (latKey: string, lngKey: string) => {
    const p = point(bag[latKey], bag[lngKey], "");
    return p ? { lat: p.lat, lng: p.lng } : undefined;
  };

  return {
    lat,
    lng,
    accuracy:
      toNumber(bag.locationAccuracy) ??
      toNumber(bag.lastAccuracy) ??
      toNumber(bag.accuracy),
    radius,
    distance,
    inside,
    geofence: which.includes("secondary")
      ? "secondary"
      : which.includes("primary")
        ? "primary"
        : undefined,
    primaryCenter:
      centre("primaryLatitude", "primaryLongitude") ??
      centre("centerLatitude", "centerLongitude"),
    secondaryCenter: centre("secondaryLatitude", "secondaryLongitude"),
  };
};

export interface EventMapLabels {
  eventPosition: string;
  primaryCentre: string;
  secondaryCentre: string;
}

/** Event detail map: the event point, the registered centre(s) and radius. */
export const buildEventMapModel = (
  location: EventLocation,
  labels: EventMapLabels,
): TroubleshootingMapModel => {
  const primary = location.primaryCenter
    ? { ...location.primaryCenter, label: labels.primaryCentre }
    : undefined;
  const secondary = location.secondaryCenter
    ? { ...location.secondaryCenter, label: labels.secondaryCentre }
    : undefined;
  const activeCenter =
    location.geofence === "secondary" ? secondary : (primary ?? secondary);
  return {
    primary,
    secondary,
    event: {
      lat: location.lat,
      lng: location.lng,
      label: labels.eventPosition,
    },
    activeCenter,
    radiusMeters: activeCenter ? location.radius : undefined,
    extraPoints: [],
    extraCircles: [],
  };
};

/** "42 s", "5 min", "3 h", "2 d" — age of a timestamp. */
export const formatAge = (iso: string, nowMs: number = Date.now()): string => {
  const ms = Math.max(0, nowMs - new Date(iso).getTime());
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h`;
  return `${Math.round(h / 24)} d`;
};
